import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import {
  DateTime,
  NaiveDate,
  NaiveDateTime,
  TimezoneInfo,
  TimezoneRegion,
  Tzname,
} from "@tento-chrono";
import { FlightInfo } from "./flight-info.ts";
import { ZoneLoad } from "@tento-lona/sheets/internal-sdk";
import type { Json } from "@tento-lona/sheets";

/**
 * Where a column falls relative to the user's flights: on the ground,
 * departing or arriving, in flight, or collapsed.
 */
export type ResolvedIndex<Info> =
  | ResolvedIndex.Transitioning
  | ResolvedIndex.Ground
  | ResolvedIndex.InFlight<Info>
  | ResolvedIndex.Collapsed<Info>;

/**
 * Types and values that go with `ResolvedIndex`: `Transitioning`, `Ground`,
 * `InFlight`, `Collapsed`, `Serialized`, `dbg`, and 1 more.
 */
export namespace ResolvedIndex {
  export type Transitioning = {
    type: "departure" | "arrival";
    date: NaiveDate;
    tp: NaiveDateTime;
    tz: TimezoneRegion;
  };

  export type Ground = {
    type: "ground";
    date: NaiveDate;
    tz: TimezoneRegion;
  };

  export type InFlight<Info> = {
    type: "in-flight";
    info: FlightInfo<Info>;
  };

  export type Collapsed<Info> = {
    type: "collapsed";
    date: NaiveDate;
    arrivalTp: NaiveDateTime;
    departureTp: NaiveDateTime;
    tz: TimezoneRegion;
    arrivalFlight: Option<FlightInfo<Info>>;
    departureFlight: Option<FlightInfo<Info>>;
  };

  export type Serialized = {
    type: "ground" | "departure" | "arrival" | "in-flight" | "collapsed";
    /** The ISO 8601 date (`YYYY-MM-DD`). */
    date: Option<string>;
    tp: Option<string>;
    tz: Option<string>;
    info: Option<FlightInfo.Serialized>;
    /** A collapsed index's arrival and departure (`null` on every other type). */
    arrivalTp: Option<string>;
    departureTp: Option<string>;
    arrivalInfo: Option<FlightInfo.Serialized>;
    departureInfo: Option<FlightInfo.Serialized>;
  };

  export function dbg<Info>(i: ResolvedIndex<Info>): string {
    switch (i.type) {
      case "arrival":
      case "departure":
        return `${i.date.toString().padStart(16)} (${i.tz.fullname} ${i.type})`;
      case "ground":
        return `${i.date.toString().padStart(16)} (${i.tz.fullname} ${i.type})`;
      case "collapsed":
        return `${i.date.toString().padStart(16)} (${i.tz.fullname} collapsed)`;
      case "in-flight":
        return `${"-".padStart(16)} (${i.type}) (${i.info.toString()})`;
    }
  }

  export async function deserialize(s: Json): Promise<ResolvedIndex<null>> {
    if (!s || typeof s !== "object") {
      throw new Error("Invalid serialized ResolvedIndex: not an object");
    }

    const { type, date, tp, tz, info } = s as Partial<Serialized>;

    if (!type) {
      throw new Error("Invalid serialized ResolvedIndex: missing type");
    }

    switch (type) {
      case "ground": {
        if (!date) {
          throw new Error("Invalid ground ResolvedIndex: missing date");
        }
        const naiveDate = NaiveDate.parse(date);
        // No zone named: the runtime's, else UTC, the fallback traced (`tz/zone-fallback`), never silently.
        const timezoneRegion = tz
          ? await ZoneLoad.opt(tz)
          : (await ZoneLoad.resolve(TimezoneInfo.local().tzname ?? Tzname.UTC, Tzname.UTC, "ground-index")).zone;

        if (tz && !timezoneRegion) {
          throw new Error(`Invalid ground ResolvedIndex: invalid timezone "${tz}"`);
        }

        return {
          type: "ground",
          date: naiveDate,
          tz: nonnull(timezoneRegion, "ResolvedIndex: ground timezone"),
        };
      }

      case "departure":
      case "arrival": {
        if (!date) {
          throw new Error(`Invalid ${type} ResolvedIndex: missing date`);
        }
        if (!tp) {
          throw new Error(`Invalid ${type} ResolvedIndex: missing tp`);
        }
        if (!tz) {
          throw new Error(`Invalid ${type} ResolvedIndex: missing tz`);
        }

        const naiveDate = NaiveDate.parse(date);
        const dateTimeResult = DateTime.fromRfc3339(tp);
        if (dateTimeResult.isErr) {
          throw new Error(`Invalid ${type} ResolvedIndex: invalid tp format`);
        }
        const naiveDateTime = dateTimeResult.exp().ndt;
        const timezoneRegion = await ZoneLoad.opt(tz);

        if (!timezoneRegion) {
          throw new Error(`Invalid ${type} ResolvedIndex: invalid timezone "${tz}"`);
        }

        return {
          type,
          date: naiveDate,
          tp: naiveDateTime,
          tz: timezoneRegion,
        };
      }

      case "in-flight": {
        if (!info) {
          throw new Error("Invalid in-flight ResolvedIndex: missing info");
        }
        const flightInfo = await FlightInfo.fromSerialized(info);
        return {
          type: "in-flight",
          info: flightInfo,
        };
      }

      case "collapsed": {
        const { arrivalTp, departureTp, arrivalInfo, departureInfo } = s as Partial<Serialized>;

        if (!date) {
          throw new Error("Invalid collapsed ResolvedIndex: missing date");
        }
        if (!arrivalTp) {
          throw new Error("Invalid collapsed ResolvedIndex: missing arrivalTp");
        }
        if (!departureTp) {
          throw new Error("Invalid collapsed ResolvedIndex: missing departureTp");
        }
        if (!tz) {
          throw new Error("Invalid collapsed ResolvedIndex: missing tz");
        }

        const naiveDate = NaiveDate.parse(date);
        const arrivalDtResult = DateTime.fromRfc3339(arrivalTp);
        const departureDtResult = DateTime.fromRfc3339(departureTp);

        if (arrivalDtResult.isErr) {
          throw new Error("Invalid collapsed ResolvedIndex: invalid arrivalTp format");
        }
        if (departureDtResult.isErr) {
          throw new Error("Invalid collapsed ResolvedIndex: invalid departureTp format");
        }

        const timezoneRegion = await ZoneLoad.opt(tz);
        if (!timezoneRegion) {
          throw new Error(`Invalid collapsed ResolvedIndex: invalid timezone "${tz}"`);
        }

        // Flight info is optional for test cases
        const arrivalFlight = arrivalInfo ? await FlightInfo.fromSerialized(arrivalInfo) : null;
        const departureFlight = departureInfo ? await FlightInfo.fromSerialized(departureInfo) : null;

        return {
          type: "collapsed",
          date: naiveDate,
          arrivalTp: arrivalDtResult.exp().ndt,
          departureTp: departureDtResult.exp().ndt,
          tz: timezoneRegion,
          arrivalFlight,
          departureFlight,
        };
      }

      default:
        throw new Error(`Invalid ResolvedIndex type: ${type}`);
    }
  }
}
